// The fleet list's peek: one drone at a glance without leaving the table. The row already
// carries almost everything; the one call is the drone's flight logs. docs/features/peek.md.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { buttonClass } from '../components/ui/Button';
import { CATEGORY } from '../components/ui/categoryColors';
import FadeIn from '../components/ui/FadeIn';
import { IconArrowUpRight } from '../components/ui/icons';
import { FOCUS } from '../components/ui/motion';
import Peek, { PeekFact, PeekSection } from '../components/ui/Peek';
import type { PeekStep } from '../components/ui/Peek';
import Skeleton from '../components/ui/Skeleton';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Tag from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import { formatLastSeen, formatTimestamp, getFullDateTime } from '../utils/date-utils';
import { formatFirmwareVersion, parseESCVersion } from '../utils/esc-utils';
import { getDroneLogs } from './fleetApi';
import type { DroneLog, FleetDrone, FleetFlag } from './types';

// Status colours are data, drawn through Tag with the word beside them. The same families as
// the table's row tones, so a row and its peek read as one thing.
const CONDITION: Record<string, { word: string; hex: string }> = {
  RTF: { word: 'Ready to fly', hex: CATEGORY.green.ink.hex },
  Flag: { word: 'Flagged', hex: CATEGORY.yellow.ink.hex },
  OOC: { word: 'Out of commission', hex: CATEGORY.red.ink.hex },
  NYC: { word: 'Not yet commissioned', hex: CATEGORY.blue.ink.hex },
};
const PYRO_HEX = CATEGORY.orange.ink.hex;
const NEUTRAL_HEX = CATEGORY.neutral.ink.hex;

const LINK = `rounded-inner text-on-surface hover:text-primary transition-colors duration-quick ease-emphasized ${FOCUS}`;

export const airtimeText = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
};

const positionText = (loc: string): string =>
  loc.split(',').map((n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(5) : n.trim())).join(', ');

const newest = (values: (string | null)[]): string | null =>
  values.reduce<string | null>((best, v) => (v && (!best || v > best) ? v : best), null);

const FlightLogs: React.FC<{ logs: DroneLog[] | null; failed: boolean; onRetry: () => void }> = ({ logs, failed, onRetry }) => (
  <FadeIn key={failed ? 'failed' : logs ? 'loaded' : 'loading'}>
    <FlightLogsBody logs={logs} failed={failed} onRetry={onRetry} />
  </FadeIn>
);

const FlightLogsBody: React.FC<{ logs: DroneLog[] | null; failed: boolean; onRetry: () => void }> = ({ logs, failed, onRetry }) => {
  if (failed) {
    return (
      <Alert tone="error" variant="note" onRetry={onRetry} className="w-full">
        Could not load the flight logs. Check your connection and try again.
      </Alert>
    );
  }
  if (!logs) {
    return (
      <div aria-busy="true" aria-label="Loading flight logs" className="space-y-2 py-1">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }
  if (logs.length === 0) {
    return <EmptyState variant="line">No flight logs uploaded yet. Upload one from the drone page.</EmptyState>;
  }
  const flown = newest(logs.map((l) => l.flight_date));
  const uploaded = newest(logs.map((l) => l.uploaded_at));
  const timed = logs.filter((l) => typeof l.duration_seconds === 'number');
  const airtime = timed.reduce((n, l) => n + (l.duration_seconds as number), 0);
  return (
    <dl className="m-0 space-y-1">
      {flown ? (
        <PeekFact label="Last logged flight" title={getFullDateTime(flown)}>{formatTimestamp(flown, 'MMM D, YYYY')}</PeekFact>
      ) : (
        <PeekFact label="Last log uploaded" title={getFullDateTime(uploaded)}>{formatTimestamp(uploaded, 'MMM D, YYYY')}</PeekFact>
      )}
      {timed.length > 0 && <PeekFact label="Logged airtime">{airtimeText(airtime)}</PeekFact>}
      <PeekFact label="Logs">{logs.length}</PeekFact>
    </dl>
  );
};

const DroneBody: React.FC<{ drone: FleetDrone; flags: FleetFlag[]; logs: DroneLog[] | null; failed: boolean; onRetry: () => void }> = ({
  drone, flags, logs, failed, onRetry,
}) => {
  const fw = formatFirmwareVersion(drone.firmware_version, drone.firmware_label);
  const esc = parseESCVersion(drone.esc_version_result, drone.esc_version_type, drone.esc_version_major, drone.esc_version_minor);
  const where = drone.last_seen_gcs_code
    ? `${drone.last_seen_gcs_code} - ${drone.last_seen_customer_name || 'Firefly'}`
    : drone.last_seen_server;

  return (
    <div className="space-y-6">
      {flags.length > 0 && (
        <PeekSection title="Flags">
          <ul className="m-0 grid list-none gap-px overflow-hidden rounded-control p-0">
            {flags.map((f) => (
              <li key={f.id} className="flex items-baseline justify-between gap-3 bg-surface-container px-3 py-2 text-sm">
                <span className="min-w-0 text-on-surface [overflow-wrap:anywhere]">{f.descript || 'No reason given'}</span>
                {f.dateflagged && (
                  <span className="flex-none text-xs tabular-nums text-on-surface-variant" title={getFullDateTime(f.dateflagged)}>
                    {formatTimestamp(f.dateflagged, 'MMM D')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </PeekSection>
      )}

      <dl className="m-0 space-y-1">
        <PeekFact label="Customer">
          {drone.customer_org_id ? (
            <Link to={`/customers/${drone.customer_org_id}`} className={LINK}>{drone.customer_org_name || 'Customer'}</Link>
          ) : (
            'Firefly Internal'
          )}
        </PeekFact>
        {drone.customer_org_id ? <PeekFact label="Ownership">{drone.is_internal_pool ? 'Lease' : 'Sale'}</PeekFact> : null}
        <PeekFact label="Project">{drone.project_assignment || 'None'}</PeekFact>
        <PeekFact label="Firmware" title={fw.title || undefined}>{drone.firmware_version ? fw.display : 'Not reported'}</PeekFact>
        {esc.display !== '-' && <PeekFact label="ESC">{esc.display}</PeekFact>}
      </dl>

      <PeekSection title="Last seen">
        {drone.last_seen_time ? (
          <dl className="m-0 space-y-1">
            <PeekFact label="When" title={getFullDateTime(drone.last_seen_time)}>{formatLastSeen(drone.last_seen_time)}</PeekFact>
            {where && <PeekFact label="Where" title={drone.last_seen_server || undefined}>{where}</PeekFact>}
            {drone.last_seen_location && (
              <PeekFact label="Position">
                <Tooltip label="Open this position in Google Maps">
                  <a
                    href={`https://www.google.com/maps?q=${encodeURIComponent(drone.last_seen_location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex max-w-full items-center gap-1 ${LINK}`}
                  >
                    <span className="truncate">{positionText(drone.last_seen_location)}</span>
                    <IconArrowUpRight className="h-3.5 w-3.5 flex-none" />
                  </a>
                </Tooltip>
              </PeekFact>
            )}
          </dl>
        ) : (
          <p className="m-0 text-sm text-on-surface-variant">Never seen on a ground station.</p>
        )}
      </PeekSection>

      <PeekSection title="Flight logs">
        <FlightLogs logs={logs} failed={failed} onRetry={onRetry} />
      </PeekSection>
    </div>
  );
};

export interface DronePeekProps {
  /** The fleet row being shown; null closes the peek. */
  drone: FleetDrone | null;
  /** The fleet store's active flags; the peek picks this drone's. */
  flags: FleetFlag[];
  onClose: () => void;
  returnTo: React.RefObject<HTMLElement | null>;
  step?: PeekStep;
}

const DronePeek: React.FC<DronePeekProps> = ({ drone, flags, onClose, returnTo, step }) => {
  const serial = drone?.serialnumber ?? null;
  // Keyed by serial, so a step never shows the last drone's logs under the next one's name.
  const [logs, setLogs] = useState<{ serial: string; rows: DroneLog[] } | null>(null);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!serial) return undefined;
    let live = true;
    setFailed(false);
    getDroneLogs(serial)
      .then((rows) => { if (live) setLogs({ serial, rows: Array.isArray(rows) ? rows : [] }); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [serial, reload]);

  const condition = drone?.condition ? CONDITION[drone.condition] : undefined;
  const mine = drone ? flags.filter((f) => f.drone_serial === drone.serialnumber && f.active) : [];

  return (
    <Peek
      open={!!drone}
      onClose={onClose}
      label={`Drone preview: ${serial || ''}`}
      title={<span className="font-mono">{serial}</span>}
      meta={drone ? (
        <>
          <Tag hex={condition?.hex || NEUTRAL_HEX}>{condition?.word || drone.condition || 'No status'}</Tag>
          {drone.pyro_enabled && <Tag hex={PYRO_HEX}>Pyro</Tag>}
        </>
      ) : null}
      returnTo={returnTo}
      step={step}
      contentKey={serial}
      footer={serial ? <Link to={`/drone/${serial}`} className={`${buttonClass('filled', 'md')} w-full`}>Open drone</Link> : null}
    >
      {drone ? (
        <DroneBody
          drone={drone}
          flags={mine}
          logs={logs && logs.serial === serial ? logs.rows : null}
          failed={failed}
          onRetry={() => setReload((n) => n + 1)}
        />
      ) : null}
    </Peek>
  );
};

export default DronePeek;
